import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageLayout from "../components/PageLayout";
import { locationPages } from "../data/locationPages";
import { SITE, ROUTES } from "../data/siteConfig";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const LocationPage = ({ locationKey }) => {
  const data = locationPages[locationKey];
  if (!data) return null;

  const canonicalUrl = `${SITE.url}${data.path}`;
  const jsonLd = [
    {
      "@type": "LocalBusiness",
      "@id": `${canonicalUrl}#localbusiness`,
      name: SITE.nameFull,
      image: `${SITE.url}/logo.svg`,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.address.street,
        addressLocality: SITE.address.city,
        postalCode: SITE.address.postalCode,
        addressRegion: SITE.address.region,
        addressCountry: "AT",
      },
      telephone: SITE.contact.phone.replace(/\s/g, ""),
      email: SITE.contact.email,
      url: SITE.url,
      priceRange: "€€",
      areaServed: data.areaName,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE.url },
        { "@type": "ListItem", position: 2, name: data.title, item: canonicalUrl },
      ],
    },
    ...(data.faqs?.length
      ? [
          {
            "@type": "FAQPage",
            mainEntity: data.faqs.map(({ q, a }) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          },
        ]
      : []),
  ];

  return (
    <>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        path={data.path}
        jsonLd={jsonLd}
      />
      <PageLayout
        breadcrumbs={[{ label: data.title }]}
        ctaTitle="Kostenlose Erstberatung"
        ctaSubtitle="Wir besprechen Ihr Projekt unverbindlich – in Wolfsberg, per Videocall oder in ganz Österreich."
      >
        <article className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f1419] mb-6">
            {data.h1}
          </h1>
          <p className="text-lg text-[#3a3a3a] leading-relaxed mb-10">
            {data.intro}
          </p>

          {data.sections?.map((section, i) => (
            <section key={i} className="mb-10">
              <h2 className="text-2xl font-semibold text-[#0f1419] mb-3">
                {section.title}
              </h2>
              <p className="text-[#3a3a3a] leading-relaxed">{section.content}</p>
            </section>
          ))}

          {data.faqs?.length > 0 && (
            <section className="mt-12" aria-labelledby="faq-heading">
              <h2 id="faq-heading" className="text-2xl font-semibold text-[#0f1419] mb-6">
                Häufig gestellte Fragen – Webdesign {data.areaName}
              </h2>
              <Accordion type="single" collapsible className="space-y-3">
                {data.faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="bg-white border border-[#e8e4dc] rounded-xl px-5 overflow-hidden"
                  >
                    <AccordionTrigger className="text-left font-semibold text-[#0f1419] hover:text-[#4a9da8] py-4 hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#3a3a3a] pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          <p className="mt-10 text-[#3a3a3a] flex flex-wrap gap-x-2 gap-y-1">
            <Link to="/#kontakt" className="text-[#4a9da8] font-semibold hover:underline">
              Kontakt aufnehmen
            </Link>
            {" · "}
            <Link to={ROUTES.digitaleVermarktung} className="text-[#4a9da8] font-semibold hover:underline">
              Digitale Vermarktung
            </Link>
            {" · "}
            <Link to={ROUTES.socialMediaMarketing} className="text-[#4a9da8] font-semibold hover:underline">
              Social Media Marketing
            </Link>
            {" · "}
            <Link to={ROUTES.serviceWebdesign} className="text-[#4a9da8] font-semibold hover:underline">
              Webdesign
            </Link>
            {" · "}
            <Link to={ROUTES.costWebdesign} className="text-[#4a9da8] font-semibold hover:underline">
              Webdesign Kosten Österreich
            </Link>
          </p>
        </article>
      </PageLayout>
    </>
  );
};

export default LocationPage;
